import { useState, useEffect } from "react";
import { X, Mic, MicOff, Save, PenTool, Sun, Cloud, CloudRain, CloudLightning, Sunrise } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import useSpeechRecognition from "../../hooks/useSpeechRecognition";
import api from "../../api/axios";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import SanctuaryWallModal from "./SanctuaryWallModal";

const PROMPTS = [
    "What made you smile today?",
    "What is one thing you are grateful for?",
    "What was a challenge you overcame?",
    "How does your heart feel right now?",
    "What is a lesson you learned recently?",
    "Describe a moment of peace you found today."
];

import { MOODS } from "../../lib/constants";

export default function DigitalDiary({ onClose }) {
    const { t } = useLanguage();
    const [content, setContent] = useState("");
    const [prompt, setPrompt] = useState("");
    const [selectedMood, setSelectedMood] = useState(null);
    const [showWall, setShowWall] = useState(false);
    const { user } = useAuth();

    // Use Shared Hook
    const { isListening, transcript, volume, startListening, stopListening, resetTranscript } = useSpeechRecognition();

    useEffect(() => {
        // Pick random prompt
        setPrompt(PROMPTS[Math.floor(Math.random() * PROMPTS.length)]);
    }, []);

    // Sync: When listening stops, commit transcript only if it hasn't been committed yet
    useEffect(() => {
        let timeout;
        if (!isListening && transcript.trim()) {
            // Use a slight delay to ensure no more incoming text
            timeout = setTimeout(() => {
                setContent((prev) => {
                    const newText = transcript.trim();
                    if (!newText) return prev;
                    
                    // Simple deduplication check: 
                    // If the content already ends with this exact transcript, don't append.
                    if (prev.endsWith(newText)) return prev;
                    
                    return (prev + " " + newText).trim();
                });
                resetTranscript();
            }, 100);
        }
        return () => clearTimeout(timeout);
    }, [isListening, transcript, resetTranscript]);

    const [isStarting, setIsStarting] = useState(false);

    const toggleListening = async () => {
        if (isListening) {
            stopListening();
        } else {
            setIsStarting(true);
            try {
                await startListening();
            } finally {
                setIsStarting(false);
            }
        }
    };

    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        // Capture any active transcript if still listening
        const finalContent = (content + (isListening ? " " + transcript : "")).trim();

        if (!finalContent) {
            toast.error(t('diary_write_prompt') || "Please write something first.");
            return;
        }

        if (user?.isGuest) {
            setShowWall(true);
            return;
        }

        setIsSaving(true);
        try {
            await api.post("/diary", {
                content: finalContent,
                mood: selectedMood,
                prompt: prompt
            });

            // Force stop listening if active
            if (isListening) stopListening();

            toast.success(t('diary_save_success') || "Entry saved to your sanctuary");
            onClose();
        } catch (e) {
            console.error("Save error:", e);
            toast.error(e.response?.data?.error || t('diary_save_error') || "Failed to save entry");
        } finally {
            setIsSaving(false);
        }
    };

    // Display: content + live transcript if listening
    const displayValue = isListening ? `${content} ${transcript}` : content;

    // Calculate dynamic style based on volume (0-100)
    const micScale = 1 + (volume / 200);

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            {/* 3D Book Container */}
                <motion.div
                    initial={{ rotateY: 90, opacity: 0 }}
                    animate={{ rotateY: 0, opacity: 1 }}
                    exit={{ rotateY: -90, opacity: 0 }}
                    transition={{ type: "spring", damping: 15, stiffness: 100 }}
                    className="relative bg-[#fdfbf7] dark:bg-gray-800 w-full max-w-2xl min-h-[500px] md:min-h-[600px] rounded-2xl md:rounded-r-3xl md:rounded-l-md shadow-2xl border-l-0 md:border-l-8 border-amber-800 dark:border-l-gray-900 flex flex-col md:flex-row overflow-hidden"
                    style={{ perspective: "1000px" }}
                >
                    {/* Left Page (Mood/Prompt) - Now stacks on top for mobile */}
                    <div className="flex flex-col w-full md:w-1/3 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 p-6 md:p-8 bg-amber-50/50 dark:bg-gray-800/50">
                        <div className="flex justify-between items-center mb-4 md:mb-6">
                            <h3 className="font-display font-bold text-xl md:text-2xl text-amber-900 dark:text-amber-100 italic">{t('diary_title')}</h3>
                            <button onClick={onClose} className="md:hidden text-gray-400 hover:text-red-500">
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Today's Prompt */}
                        <div className="mb-4 md:mb-8 bg-white dark:bg-gray-700/5 p-4 rounded-xl border border-amber-100/50 dark:border-gray-600 shadow-sm">
                            <div className="text-[10px] font-bold text-amber-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <Sun className="w-3 h-3" /> {t('diary_prompt_title')}
                            </div>
                            <p className="text-amber-900 dark:text-amber-100 font-serif italic text-sm md:text-base leading-relaxed">"{prompt}"</p>
                        </div>

                        {/* Mood Selector - Horizontal scroll on mobile, flex-wrap on desktop */}
                        <div className="mb-4 md:mb-8">
                            <div className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-4">{t('diary_weather_title')}</div>
                            <div className="flex md:flex-wrap gap-3 overflow-x-auto md:overflow-visible pb-3 md:pb-0 scrollbar-hide scroll-smooth px-1">
                                {MOODS.map((m) => (
                                    <button
                                        key={m.label}
                                        onClick={() => setSelectedMood(m.label)}
                                        className={`shrink-0 flex flex-col items-center gap-2 p-3 rounded-2xl transition-all active:scale-90 ${selectedMood === m.label 
                                            ? "bg-white dark:bg-gray-700 shadow-lg ring-2 ring-amber-400/50 scale-105" 
                                            : "bg-white/30 dark:bg-gray-800/20 hover:bg-white/50 dark:hover:bg-gray-700 opacity-60 hover:opacity-100"}`}
                                        title={t(m.titleKey || 'mood_' + m.id)}
                                    >
                                        <m.icon className={`w-5 h-5 md:w-6 md:h-6 ${m.color}`} />
                                    </button>
                                ))}
                            </div>
                            
                            {/* Selected Mood Label - Critical for Mobile where tooltips don't exist */}
                            <AnimatePresence mode="wait">
                                {selectedMood && (
                                    <motion.div
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 10 }}
                                        className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100/50 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/50 rounded-xl"
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-widest text-amber-700/60 dark:text-amber-400/40">{t('diary_mood_label') || "Mood"}:</span>
                                        <span className="text-xs font-bold text-amber-900 dark:text-amber-100">{t('mood_' + (selectedMood || '').toLowerCase()) || selectedMood}</span>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        <div className="mt-auto hidden md:block">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{t('diary_entry_date')}</div>
                            <div className="text-sm font-serif text-gray-800 dark:text-gray-200">
                                {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                            </div>
                        </div>
                    </div>

                    {/* Right Page (Writing Area) */}
                    <div className="flex-1 p-6 md:p-10 flex flex-col relative bg-[linear-gradient(to_bottom,transparent_29px,#e5e7eb_30px)] bg-[length:100%_30px] dark:bg-[linear-gradient(to_bottom,transparent_29px,#333_30px)]">
                        <button onClick={onClose} className="hidden md:block absolute top-6 right-6 text-gray-400 hover:text-red-500 transition-colors">
                            <X className="w-6 h-6" />
                        </button>

                        <textarea
                            value={displayValue}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder={t('diary_placeholder') || "Begin your reflection..."}
                            className="flex-1 w-full bg-transparent border-none resize-none focus:ring-0 focus:outline-none text-lg leading-[30px] text-gray-800 dark:text-gray-100 font-serif placeholder:italic placeholder:text-gray-300 dark:placeholder:text-gray-600"
                            autoFocus
                        />

                    {/* Toolbar */}
                    <div className="flex items-center justify-between mt-4 md:mt-0 pt-4 border-t border-transparent">
                        <button
                            onClick={toggleListening}
                            disabled={isStarting}
                            style={{
                                transform: isListening ? `scale(${micScale})` : 'scale(1)',
                                boxShadow: isListening ? `0 0 ${volume}px rgba(220, 38, 38, 0.4)` : 'none'
                            }}
                            className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-75 ${isListening ? "bg-red-100 text-red-600 border border-red-200" : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"} ${isStarting ? "opacity-50 cursor-wait" : ""}`}
                            title={isListening ? t('diary_stop_tooltip') || "Stop Recording" : t('diary_start_tooltip') || "Start Voice Input"}
                        >
                            {isStarting ? (
                                <div className="w-5 h-5 border-2 border-gray-400 border-t-brand-blue animate-spin rounded-full" />
                            ) : isListening ? (
                                <>
                                    <span className="w-2 h-2 bg-red-600 rounded-sm" />
                                    <span className="text-sm font-medium">{t('diary_stop_label') || "Stop"}</span>
                                </>
                            ) : (
                                <Mic className="w-5 h-5" />
                            )}
                        </button>

                        <button
                            onClick={handleSave}
                            className="flex items-center gap-2 px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-full font-medium shadow-lg hover:shadow-xl transition-all"
                        >
                            <PenTool className="w-4 h-4" />
                            {t('diary_pen_down')}
                        </button>
                    </div>
                </div>
            </motion.div>
            <SanctuaryWallModal isOpen={showWall} onClose={() => setShowWall(false)} />
        </div>
    );
}
